export type GuidedActionCategory =
  'commerciale' | 'operativita' | 'collaborazione' | 'amministrazione' | 'configurazione';

export interface GuidedActionStep {
  title: string;
  description: string;
}

export interface GuidedAction {
  id: string;
  title: string;
  description: string;
  outcome: string;
  category: GuidedActionCategory;
  navigationId: string;
  href: string;
  permission: string;
  create?: boolean;
  ctaLabel: string;
  keywords: readonly string[];
  steps: readonly GuidedActionStep[];
}

export interface WorkflowStage {
  id: string;
  label: string;
  navigationId: string;
  href: string;
  description: string;
}

export const guidedActionCategories: ReadonlyArray<{
  id: GuidedActionCategory;
  label: string;
}> = [
  { id: 'commerciale', label: 'Relazioni e vendite' },
  { id: 'operativita', label: 'Lavoro e consuntivi' },
  { id: 'collaborazione', label: 'Note e collaborazione' },
  { id: 'amministrazione', label: 'Fatturazione e spese' },
  { id: 'configurazione', label: 'Configurazione' }
];

/**
 * Percorsi basati esclusivamente su funzioni effettivamente disponibili nelle pagine del prodotto.
 * Il catalogo non simula esecuzioni: accompagna l'utente alla pagina autorizzata e descrive i
 * controlli che troverà nell'interfaccia.
 */
export const guidedActions: readonly GuidedAction[] = [
  {
    id: 'qualificare-opportunita',
    title: 'Qualificare una nuova opportunità',
    description:
      'Inserisci una trattativa, definisci valore e prossima azione e seguila nella pipeline.',
    outcome: 'Una opportunità tracciata nel CRM, pronta per avanzare di fase.',
    category: 'commerciale',
    navigationId: 'crm',
    href: '/crm',
    permission: 'crm.opportunity.manage',
    create: true,
    ctaLabel: 'Crea una opportunità',
    keywords: ['lead', 'trattativa', 'vendita', 'pipeline', 'preventivo', 'prospect'],
    steps: [
      {
        title: 'Apri il CRM',
        description: 'Seleziona “Nuova opportunità” dalla pagina Pipeline CRM.'
      },
      {
        title: 'Descrivi la trattativa',
        description: 'Indica titolo, valore, valuta, probabilità, data prevista e prossima azione.'
      },
      {
        title: 'Segui l’avanzamento',
        description:
          'Aggiorna la fase nella pipeline; per creare un progetto collega prima un cliente.'
      }
    ]
  },
  {
    id: 'creare-cliente',
    title: 'Creare l’anagrafica di un cliente',
    description:
      'Registra dati di contatto e fiscali per collegare il cliente al lavoro successivo.',
    outcome: 'Una anagrafica cliente disponibile per opportunità e progetti.',
    category: 'commerciale',
    navigationId: 'clients',
    href: '/clients',
    permission: 'clients.client.manage',
    create: true,
    ctaLabel: 'Crea un cliente',
    keywords: ['azienda', 'persona', 'contatto', 'anagrafica', 'partita iva', 'referente'],
    steps: [
      {
        title: 'Apri Clienti',
        description: 'Seleziona “Nuovo cliente” dalla pagina delle anagrafiche.'
      },
      {
        title: 'Inserisci i dati utili',
        description: 'Scegli tipo e stato, poi compila nome, contatti e gli eventuali dati fiscali.'
      },
      {
        title: 'Salva l’anagrafica',
        description: 'Il cliente diventa selezionabile nei flussi che prevedono un collegamento.'
      }
    ]
  },
  {
    id: 'avviare-progetto',
    title: 'Avviare un progetto per un cliente',
    description: 'Definisci obiettivo, priorità, calendario e budget operativo del progetto.',
    outcome: 'Un progetto condiviso a cui collegare attività e consuntivi.',
    category: 'operativita',
    navigationId: 'projects',
    href: '/projects',
    permission: 'projects.project.manage',
    create: true,
    ctaLabel: 'Crea un progetto',
    keywords: [
      'commessa',
      'lavoro',
      'budget',
      'scadenza',
      'cliente',
      'team',
      'registra nuovo progetto',
      'registrare progetto',
      'creare progetto'
    ],
    steps: [
      {
        title: 'Apri Progetti',
        description: 'Seleziona “Nuovo progetto” e assegna un nome riconoscibile.'
      },
      {
        title: 'Collega il contesto',
        description: 'Scegli il cliente e indica descrizione, stato, priorità e date.'
      },
      {
        title: 'Definisci il perimetro',
        description: 'Aggiungi budget, valuta, timezone e tag prima di salvare.'
      }
    ]
  },
  {
    id: 'pianificare-attivita',
    title: 'Pianificare una attività',
    description: 'Crea un elemento di lavoro con priorità, scadenza, stima ed etichette.',
    outcome: 'Una attività visibile nelle viste personali e di team.',
    category: 'operativita',
    navigationId: 'tasks',
    href: '/tasks',
    permission: 'tasks.task.manage',
    create: true,
    ctaLabel: 'Crea una attività',
    keywords: ['task', 'todo', 'assegnare', 'scadenza', 'priorità', 'stima'],
    steps: [
      {
        title: 'Apri Attività',
        description: 'Seleziona “Nuova attività” dalla vista che stai usando.'
      },
      {
        title: 'Pianifica il lavoro',
        description: 'Inserisci titolo, stato, priorità, scadenza e stima in minuti.'
      },
      {
        title: 'Rendi il lavoro rintracciabile',
        description: 'Aggiungi etichette utili e crea l’attività.'
      }
    ]
  },
  {
    id: 'registrare-tempo',
    title: 'Registrare il tempo lavorato',
    description: 'Usa il timer oppure inserisci manualmente un intervallo già concluso.',
    outcome: 'Ore registrate e disponibili nei report del periodo.',
    category: 'operativita',
    navigationId: 'time',
    href: '/time',
    permission: 'time.entry.manage',
    create: true,
    ctaLabel: 'Inserisci ore manualmente',
    keywords: ['ore', 'timer', 'timesheet', 'consuntivo', 'fatturabile', 'registrazione manuale'],
    steps: [
      {
        title: 'Scegli la modalità',
        description: 'Avvia il timer per il lavoro corrente oppure apri l’inserimento manuale.'
      },
      {
        title: 'Descrivi l’intervallo',
        description: 'Per l’inserimento manuale indica data, descrizione, inizio e fine.'
      },
      {
        title: 'Controlla il consuntivo',
        description: 'Verifica la registrazione e consulta i report per periodo e progetto.'
      }
    ]
  },
  {
    id: 'programmare-fatturazione',
    title: 'Programmare una voce di fatturazione',
    description: 'Registra una entrata o un costo atteso, una tantum oppure ricorrente.',
    outcome: 'Una voce con importo, valuta e prossima scadenza monitorabili.',
    category: 'amministrazione',
    navigationId: 'billing',
    href: '/billing',
    permission: 'billing.item.manage',
    create: true,
    ctaLabel: 'Crea una scadenza',
    keywords: [
      'fattura',
      'preparare fattura',
      'addebito',
      'scadenza',
      'ricorrente',
      'entrata',
      'costo',
      'rinnovo'
    ],
    steps: [
      {
        title: 'Apri Fatturazione',
        description: 'Nella pagina Scadenze seleziona “Nuova voce”.'
      },
      {
        title: 'Definisci la voce',
        description: 'Scegli direzione e frequenza, poi indica nome, controparte, importo e valuta.'
      },
      {
        title: 'Imposta la scadenza',
        description: 'Indica prossimo addebito, data iniziale e giorni di tolleranza.'
      }
    ]
  },
  {
    id: 'inserire-spesa',
    title: 'Inserire una nota spese',
    description: 'Registra una uscita con fornitore, importo, data e metodo di pagamento.',
    outcome: 'Una spesa pronta per il flusso di approvazione e rimborso.',
    category: 'amministrazione',
    navigationId: 'expenses',
    href: '/expenses',
    permission: 'expenses.expense.manage',
    create: true,
    ctaLabel: 'Inserisci una spesa',
    keywords: ['ricevuta', 'scontrino', 'rimborso', 'trasferta', 'fornitore', 'pagamento'],
    steps: [
      {
        title: 'Apri Spese',
        description: 'Seleziona “Nuova spesa” dalla pagina Note spese.'
      },
      {
        title: 'Inserisci i dati',
        description: 'Compila categoria, fornitore, importo, valuta, data e metodo di pagamento.'
      },
      {
        title: 'Completa la registrazione',
        description: 'Aggiungi una descrizione, indica se è rimborsabile e salva la spesa.'
      }
    ]
  },
  {
    id: 'collaborare-nota',
    title: 'Collaborare in tempo reale su una nota',
    description:
      'Lavora sullo stesso documento con un collega e verifica presenza e modifiche condivise.',
    outcome: 'Contenuto sincronizzato in tempo reale e conservato per le aperture successive.',
    category: 'collaborazione',
    navigationId: 'notes',
    href: '/notes',
    permission: 'notes.document.manage',
    create: true,
    ctaLabel: 'Crea una nota condivisa',
    keywords: ['nota', 'documento', 'testo', 'collaborare', 'presenza', 'tempo reale', 'yjs'],
    steps: [
      {
        title: 'Crea o apri una nota',
        description: 'Dalla pagina Note crea un documento oppure selezionane uno già disponibile.'
      },
      {
        title: 'Apri lo stesso documento',
        description: 'Un altro utente autorizzato apre la stessa nota dal proprio workspace.'
      },
      {
        title: 'Collabora e verifica',
        description:
          'Scrivi nel documento, controlla gli indicatori di presenza e riaprilo per verificare la persistenza.'
      }
    ]
  },
  {
    id: 'gestire-cartelle-note',
    title: 'Organizzare le note in cartelle',
    description:
      'Crea, rinomina o elimina una cartella per ordinare i documenti nel workspace corrente.',
    outcome: 'Note raggruppate in una struttura chiara senza creare un nuovo workspace ERP.',
    category: 'collaborazione',
    navigationId: 'notes',
    href: '/notes',
    permission: 'notes.document.manage',
    ctaLabel: 'Gestisci le cartelle',
    keywords: ['cartella', 'nuova cartella', 'rinomina', 'elimina', 'organizza note', 'spazi note'],
    steps: [
      {
        title: 'Apri Note',
        description: 'Nella sidebar del modulo individua la sezione Cartelle.'
      },
      {
        title: 'Crea o modifica',
        description: 'Usa “Nuova cartella” oppure il menu della cartella per rinominarla.'
      },
      {
        title: 'Elimina con consapevolezza',
        description:
          'Conferma l’eliminazione: le note contenute restano disponibili in Tutte le note.'
      }
    ]
  },
  {
    id: 'creare-workspace-erp',
    title: 'Creare un altro workspace ERP',
    description:
      'Aggiungi un confine indipendente per dati, persone, moduli e permessi di un’altra organizzazione.',
    outcome: 'Un workspace separato e pronto per essere aperto dallo switcher.',
    category: 'configurazione',
    navigationId: 'workspaces',
    href: '/admin/workspaces',
    permission: 'installation.workspace.manage',
    create: true,
    ctaLabel: 'Crea un workspace ERP',
    keywords: ['nuovo workspace', 'altra azienda', 'multi workspace', 'spazio separato'],
    steps: [
      {
        title: 'Apri Workspace ERP',
        description: 'La voce si trova nella sezione Sistema ed è riservata al super-admin.'
      },
      {
        title: 'Definisci il contesto',
        description: 'Inserisci nome, slug, fuso orario, lingua e valuta.'
      },
      {
        title: 'Crea e apri',
        description: 'Salva, poi usa Apri per passare al nuovo contesto dati.'
      }
    ]
  },
  {
    id: 'invitare-persona',
    title: 'Invitare una persona nel workspace',
    description: 'Invia un link personale all’email con cui la persona effettuerà l’accesso.',
    outcome: 'Una nuova membership base, pronta per ricevere moduli e permessi.',
    category: 'configurazione',
    navigationId: 'access',
    href: '/admin/access',
    permission: 'users.permission.manage',
    ctaLabel: 'Invita una persona',
    keywords: ['invito', 'nuovo utente', 'membro', 'email', 'accesso workspace'],
    steps: [
      {
        title: 'Inserisci l’email',
        description: 'In Accessi scegli Invita persona e usa l’email esatta dell’identità OIDC.'
      },
      {
        title: 'Fai aprire il collegamento',
        description: 'Il destinatario effettua il login e conferma Accetta invito.'
      },
      {
        title: 'Controlla gli accessi',
        description: 'Dopo l’ingresso assegna i moduli e i permessi necessari.'
      }
    ]
  },
  {
    id: 'personalizzare-workspace',
    title: 'Personalizzare workspace, lingua e branding',
    description:
      'Configura le preferenze del workspace tramite definizioni tipizzate e valori salvati dal server.',
    outcome:
      'Preferenze salvate; i servizi esterni sono verificati soltanto dopo un test concluso con successo.',
    category: 'configurazione',
    navigationId: 'settings',
    href: '/settings',
    permission: 'settings.workspace.manage',
    ctaLabel: 'Apri Impostazioni',
    keywords: ['workspace', 'lingua', 'locale', 'timezone', 'fuso', 'logo', 'colore', 'branding'],
    steps: [
      {
        title: 'Scegli una sezione',
        description: 'Apri Branding oppure Lingua e fuso nella pagina Impostazioni.'
      },
      {
        title: 'Modifica e salva',
        description: 'Aggiorna soltanto i valori necessari e conferma con “Salva modifiche”.'
      },
      {
        title: 'Verifica le integrazioni separatamente',
        description:
          'Per SMTP, webhook o storage, “Impostazioni salvate” conferma solo la configurazione: usa il test disponibile e controllane l’esito prima di considerare attivo il servizio.'
      }
    ]
  },
  {
    id: 'modificare-record-concorrenza',
    title: 'Modificare un record senza sovrascrivere un collega',
    description:
      'Aggiorna un cliente esistente usando il controllo di versione applicato dal server.',
    outcome:
      'Il record viene aggiornato solo se la versione è corrente; una modifica concorrente non viene sovrascritta in silenzio.',
    category: 'configurazione',
    navigationId: 'clients',
    href: '/clients',
    permission: 'clients.client.manage',
    ctaLabel: 'Apri i clienti esistenti',
    keywords: ['modifica', 'record', 'tabella', 'versione', 'etag', 'concorrenza', 'conflitto'],
    steps: [
      {
        title: 'Apri un record esistente',
        description:
          'Nella tabella Clienti seleziona il comando di modifica sulla riga interessata.'
      },
      {
        title: 'Salva la modifica',
        description: 'DDone invia al server la versione caricata insieme ai nuovi valori.'
      },
      {
        title: 'Gestisci un eventuale conflitto',
        description:
          'Se il record è cambiato nel frattempo, ricarica i dati e riapplica consapevolmente la modifica.'
      }
    ]
  }
];

export const workflowStages: readonly WorkflowStage[] = [
  {
    id: 'crm',
    label: 'CRM',
    navigationId: 'crm',
    href: '/crm',
    description: 'Qualifica la richiesta'
  },
  {
    id: 'clients',
    label: 'Clienti',
    navigationId: 'clients',
    href: '/clients',
    description: 'Consolida l’anagrafica'
  },
  {
    id: 'projects',
    label: 'Progetti',
    navigationId: 'projects',
    href: '/projects',
    description: 'Organizza la commessa'
  },
  {
    id: 'tasks',
    label: 'Attività',
    navigationId: 'tasks',
    href: '/tasks',
    description: 'Pianifica il lavoro'
  },
  {
    id: 'time',
    label: 'Tempo',
    navigationId: 'time',
    href: '/time',
    description: 'Registra il consuntivo'
  },
  {
    id: 'billing',
    label: 'Fatturazione',
    navigationId: 'billing',
    href: '/billing',
    description: 'Programma entrate e costi'
  },
  {
    id: 'expenses',
    label: 'Spese',
    navigationId: 'expenses',
    href: '/expenses',
    description: 'Raccogli e approva le uscite'
  }
];

export function guidedActionsForNavigation(
  navigationIds: ReadonlySet<string>,
  actions: readonly GuidedAction[] = guidedActions,
  hasPermission: (permission?: string) => boolean = () => true
): GuidedAction[] {
  return actions.filter(
    (action) => navigationIds.has(action.navigationId) && hasPermission(action.permission)
  );
}

export function searchGuidedActions(
  actions: readonly GuidedAction[],
  query: string,
  category?: GuidedActionCategory
): GuidedAction[] {
  const normalizedQuery = normalizeSearchText(query);
  return actions.filter((action) => {
    if (category && action.category !== category) return false;
    if (!normalizedQuery) return true;
    const searchable = normalizeSearchText(
      [
        action.title,
        action.description,
        action.outcome,
        ...action.keywords,
        ...action.steps.flatMap((step) => [step.title, step.description])
      ].join(' ')
    );
    const meaningfulTerms = normalizedQuery
      .split(' ')
      .filter((term) => !['a', 'al', 'alla', 'di', 'il', 'la', 'le', 'un', 'una'].includes(term));
    return (
      searchable.includes(normalizedQuery) ||
      meaningfulTerms.every((term) => searchable.includes(term))
    );
  });
}

export function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('it')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}
